import {
  addDays,
  addMinutes,
  differenceInMinutes,
  format,
  isValid,
  parse,
  setHours,
  setMinutes,
  setSeconds,
} from 'date-fns';

import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import { ClaimEigenschappen } from '@/queries/mutaties/opdrachten';
import { formatIsoDate, startEindDatumTijdVerschillen } from '@/utils/dateUtils';

import {
  type ClaimPostStateType,
  type ClaimReducerActionType,
  type CreateClaimFormHelperType,
  defaultState,
} from './types';

export const parseInput = (datum: string, tijd: string): Date =>
  parse(`${datum} ${tijd}:00`, `yyyy-MM-dd HH:mm:ss`, new Date());
const formatTime = (date?: Date, invalidValue = ''): string =>
  isValid(date) ? format(date as Date, `HH:mm`) : invalidValue;
const validateTime = (time: string): boolean => Boolean(new RegExp(/\d{2}:\d{2}/g).exec(time) !== null);

const getDefaultDatetimes = (
  beginDatumTijdDate?: Date,
  eindDatumTijdDate?: Date,
  isUpdate?: boolean,
): CreateClaimFormHelperType & { [key: string]: string | Date | undefined } => {
  const [beginValid, eindValid] = [isValid(beginDatumTijdDate), isValid(eindDatumTijdDate)];
  const times = {
    beginDatumTijd: '',
    eindDatumTijd: '',
    beginDatum: '',
    beginTijd: '',
    eindDatum: '',
    eindTijd: '',
    beginDatumTijdDate,
    eindDatumTijdDate,
  };

  if (beginValid && eindValid && !isUpdate) {
    const [begin, eind] = [times.beginDatumTijdDate, times.eindDatumTijdDate] as Date[];
    // set default times
    const diffMin = -1 * differenceInMinutes(begin, eind);

    if (diffMin >= 11) {
      times.beginDatumTijdDate = addMinutes(begin, 5);
      times.eindDatumTijdDate = addMinutes(eind, -5);
    } else if (diffMin < 11 && diffMin >= 6) {
      times.beginDatumTijdDate = addMinutes(begin, 5);
      times.eindDatumTijdDate = addMinutes(eind, -1);
    } else if (diffMin < 6 && diffMin >= 4) {
      times.beginDatumTijdDate = addMinutes(begin, 3);
    }
  } else if (!beginValid && !eindValid && !isUpdate) {
    times.beginDatumTijdDate = new Date();
    times.eindDatumTijdDate = setSeconds(setMinutes(setHours(addDays(new Date(), 1), 2), 0), 0);
  }
  if (beginValid) times.beginDatumTijd = (times.beginDatumTijdDate as Date)?.toISOString() ?? '';
  if (eindValid) times.eindDatumTijd = (times.eindDatumTijdDate as Date)?.toISOString() ?? '';

  // Vertaal naar formulier velden
  times.beginDatum = formatIsoDate(times.beginDatumTijdDate);
  times.beginTijd = formatTime(times.beginDatumTijdDate);
  times.eindDatum = formatIsoDate(times.eindDatumTijdDate);
  times.eindTijd = formatTime(times.eindDatumTijdDate);

  return times;
};

const validateDateTimeInput = (state: ClaimPostStateType, field: keyof CreateClaimFormHelperType) => {
  state.fieldErrors = {}; // re-assess errors every time;
  const [begin, eind] = [parseInput(state.beginDatum, state.beginTijd), parseInput(state.eindDatum, state.eindTijd)];

  // valideerDatum en pas aan als nodig
  let dateMeta = startEindDatumTijdVerschillen(begin, eind);

  if (dateMeta.isAfter) {
    if (field === 'beginDatum') state.eindDatum = state.beginDatum;
    if (field === 'eindDatum') state.beginDatum = state.eindDatum;
  }

  // Valideer na automatische aanpassing van de datum nog een keer
  dateMeta = startEindDatumTijdVerschillen(
    parseInput(state.beginDatum, state.beginTijd),
    parseInput(state.eindDatum, state.eindTijd),
  );

  if (dateMeta.isAfter) {
    if (field === 'beginTijd' || 'beginDatum') state.fieldErrors.beginTijd = `Tijd moet voor 'Tot tijd' liggen.`;
    if (field === 'eindTijd' || 'eindDatum') state.fieldErrors.eindTijd = `Tijd moet na 'Van tijd' liggen.`;
  }

  if (dateMeta.isLessThan1Minute) {
    state.fieldErrors[field.replace('Datum', 'Tijd')] = `Tijdsverschil moet minimaal 1 min. zijn.`;
  }

  return state;
};

/** Create a Claim form state from the dialog menu context details.
 *  State initializer function (3rd argument of useReducer).
 *
 * Note: tricky inversion of usage of vertrekTijd
 *    Claim from Overgang:
 *      <---wle-1---aankomst-> Overgang <-vertrek---wle-2--->
 *    Claim from deeltraject:
 *      <-vertrek---claim---aankomst->
 * @param details
 * @returns
 */
export const detailsToClaimState = (details: WerklijnContextMenuDetails): ClaimPostStateType => {
  const times = details.isClaimUpdate
    ? getDefaultDatetimes(details?.vertrekTijd, details?.aankomstTijd, details.isClaimUpdate)
    : getDefaultDatetimes(details?.aankomstTijd, details?.vertrekTijd);

  const { beginDatum, beginTijd, eindDatum, eindTijd } = times;
  const state: ClaimPostStateType = {
    ...defaultState(details.werklijnId as string, details.werklijnVersie as number),
    beginDatum,
    beginTijd,
    eindDatum,
    eindTijd,
    claimType: details?.plaatsConflictNaar ? 'RC' : 'MC',
    isUpdate: !!details.isClaimUpdate,
  };

  if (details?.naInzetId) state.naInzetId = details.naInzetId;
  if (state.isUpdate) state.inzetId = details.wleId ?? null;

  // set default Claim values
  const eigenschappen = {
    ...state.eigenschappen,
    vertrek: details?.dienstregelpunt ?? '',
    aankomst: details?.plaatsConflictNaar ? (details?.dienstregelpuntVolgendeInzet ?? '') : '',
    beginDatumTijd: times.beginDatumTijd as string,
    eindDatumTijd: times.eindDatumTijd as string,
    materieelsoortCode: details?.materieelSoort ?? '',
    materieelAanduiding: details?.materieelAanduiding ?? '',
  };
  if (state.isUpdate) {
    eigenschappen.aankomst = details.dienstregelpuntVolgendeInzet ?? '';
  }

  if (details?.claimsoort) eigenschappen.claimsoort = details.claimsoort;
  if (details?.toelichting) eigenschappen.toelichting = details.toelichting;
  if (details?.bijzonderheidscode) eigenschappen.bijzonderheidscode = details.bijzonderheidscode;
  return { ...state, eigenschappen };
};

/** Handle form input / change events that might manipulate other state properties.
 *  State dispatch function (1st argument of useReducer).
 *
 * @param state
 * @param action
 * @returns
 */
const claimDialogReducer = (state: ClaimPostStateType, action: ClaimReducerActionType): ClaimPostStateType => {
  const newState = structuredClone(state);

  const getValue = <T,>(): T => action?.value as T;

  if (action.type === 'updateDateTime') {
    const field = action.field as keyof CreateClaimFormHelperType;
    const value = getValue<string>();
    newState[field] = value;
    if (['beginTijd', 'eindTijd'].includes(field) && !validateTime(value)) {
      return newState;
    }
    return validateDateTimeInput(newState, field);
  }

  switch (action.type) {
    case 'invalidate':
      newState.fieldErrors[action.field as keyof CreateClaimFormHelperType] = getValue<string>();
      break;
    case 'updateDetails':
      return detailsToClaimState({ ...getValue<WerklijnContextMenuDetails>() });
    case 'update':
      newState.fieldErrors[action.field as keyof CreateClaimFormHelperType] = '';
      newState.eigenschappen[action.field as keyof ClaimEigenschappen] = getValue<string>();
      if (action.field === 'aankomst') {
        newState.claimType =
          getValue<string>() === '' || getValue<string>() === newState.eigenschappen.vertrek ? 'MC' : 'RC';

        if (!newState.isUpdate) newState.eigenschappen.claimsoort = '.'; // reset naar de default waarde.
      }
      break;
    case 'reset':
      return defaultState(newState.betrokkenWerklijn.id, newState.betrokkenWerklijn.versie);
  }
  return newState;
};
export default claimDialogReducer;
